import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { Analytics, getCurrentPlatformLink } from '@bpm-web-app/utils';
import { UserPlaylistWithUser, UserPlaylistCollaboration, UserPlaylistCollaborationAccessLevel } from '@bpm-web-app/download-api-sdk';
import { UserPlaylistWithMedia } from '@bpm-web-app/stream-api-sdk';
import { useUserPlaylists } from '../shared/three-dots-sheet/useUserPlaylists';
import styles from './nav-my-playlist-list.module.css';

// eslint-disable-next-line import/no-cycle
import { LocalSearchSort } from '../nav-my-playlist-categories/nav-my-playlist-categories';
import { ActionModal } from '../shared/action-modal/action-modal';
import { CollaborationModal } from '../shared/collaboration/collaboration-modal';
import { CustomIcon } from '../shared/custom-icon/custom-icon';
import Droppable, { ALL_TYPES_NO_ARTIST } from '../droppable/droppable';

export interface NavMyPlaylistListProps {
    categoryId: number;
    sort: LocalSearchSort | undefined;
    isDragging?: boolean;
}

export function NavMyPlaylistList({ categoryId, sort, isDragging = false }: NavMyPlaylistListProps) {
    const router = useRouter();
    const { userPlaylists, reload } = useUserPlaylists(categoryId, true);
    const [playlists, setPlaylists] = useState<UserPlaylistWithUser[]>([]);
    const [isMyPlaylist, setIsMyPlaylist] = useState(false);
    const [currentPlaylist, setCurrentPlaylist] = useState<UserPlaylistWithUser | undefined>();
    const [showAddCollaborators, setShowAddCollaborators] = useState(false);
    const [playlistCollaborators, setPlaylistCollaborators] = useState<UserPlaylistCollaboration[]>();
    const [invitedCollaborators, setInvitedCollaborators] = useState<UserPlaylistCollaboration[]>([]);

    useEffect(() => {
        const listener = () => {
            reload();
        };
        document.addEventListener('playlist-update', listener);
        return () => {
            document.removeEventListener('playlist-update', listener);
        };
    }, [categoryId, reload]);

    const userPlaylistsList = useMemo(() => {
        if (sort) {
            switch (sort) {
                case 'most_recent':
                    return playlists.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
                case 'title_asc':
                    return playlists.sort((a, b) => (a.title > b.title ? 1 : -1));
                case 'title_desc':
                    return playlists.sort((a, b) => (a.title < b.title ? 1 : -1));
                default:
                    return playlists;
            }
        }

        return playlists;
    }, [sort, playlists]);

    useEffect(() => {
        if (userPlaylists && userPlaylists.data) {
            setPlaylists(userPlaylists.data as UserPlaylistWithUser[]);
        }
    }, [userPlaylists]);

    const handleClick = (path: string) => {
        router.push(path);
    };

    // eslint-disable-next-line @typescript-eslint/dot-notation
    const isActive = useCallback((id: string) => router.query['playlistId'] && router.query['playlistId'] === id, [router]);

    return (
        <>
            {userPlaylistsList.map((item) => (
                <Droppable
                    key={item.id}
                    accept={ALL_TYPES_NO_ARTIST}
                    onDrop={() => {
                        return { target: 'playlist', id: item.id };
                    }}
                    disabled={item.access_level === UserPlaylistCollaborationAccessLevel.View}>
                    {(state) => (
                        <button
                            type="button"
                            disabled={isDragging && item.access_level === UserPlaylistCollaborationAccessLevel.View}
                            aria-label="Go to playlist"
                            onClick={() => {
                                handleClick(getCurrentPlatformLink(`/my-playlist/${item.id}`));
                                Analytics.trackClick('my_playlist', item.id, { location: 'nav_my_playlist' });
                            }}
                            className={classNames(styles['nav-my-playlist-list-item'], styles['nav-my-playlist-list-item--link'], styles['nav-my-playlist-list-item--no-overflow'], {
                                [styles['nav-my-playlist-list-item--is-dragging']]: isDragging,
                                [styles['nav-my-playlist-list-item--is-dragging--over']]: state.isOver,
                                [styles['nav-my-playlist-list-item--active']]: isActive(item.id),
                            })}
                        >
                            <span className={styles['nav-my-playlist-list-item__playlist-folder']}>{item.title}</span>
                            {item.users && item.users.length > 0 ? (
                                <CustomIcon
                                    type="shared-with-me"
                                    isIconHoverActive={isActive((item.id)) || false}
                                    tooltip="Collaborators Added"
                                    color="boulder"
                                />
                            ) : null}
                        </button>

                    )}
                </Droppable>
            ))}

            <ActionModal
                hideCancel
                headerTitle={`Share Playlist ‘${currentPlaylist?.title}’`}
                onClose={() => setShowAddCollaborators(false)}
                variant="dark"
                isOpen={showAddCollaborators}
            >
                {currentPlaylist && showAddCollaborators ? (
                    <CollaborationModal
                        playlistId={currentPlaylist?.id}
                        invitedCollaborators={invitedCollaborators}
                        setInvitedCollaborators={setInvitedCollaborators}
                        setCollaborators={setPlaylistCollaborators}
                        onClose={() => setShowAddCollaborators(false)}
                        collaborators={playlistCollaborators}
                        playlistOwner={currentPlaylist.owner || undefined}
                        isMyPlaylist={isMyPlaylist}
                        invitationLink={currentPlaylist.invitation_link}
                    />
                ) : null}
            </ActionModal>
        </>
    );
}

export default NavMyPlaylistList;
